import React, { useState, useEffect } from 'react';
import { uploadFile, getConfig, saveConfig } from '../api/api';
import {
    Box, Typography, Paper, Grid, Button, TextField, Select, MenuItem,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, IconButton, CircularProgress, FormControl, InputLabel, Divider,
    Tooltip, Chip, Stack
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    Save as SaveIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Edit as EditIcon,
    TableChart as TableChartIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    LocalOffer as TagIcon,
    Computer as LaptopIcon,
    FileCopy as CopyIcon,
    CalendarToday as CalendarIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useCompetence } from '../contexts/CompetenceContext';

// Chave para armazenar a lista de bases de rateio dinâmicas
const DEFAULT_BASES_CONFIG_KEY = 'dynamic_bases_config';

// Define a URL base da API
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TabelasAuxiliaresPage = () => {
    const { globalCompetence } = useCompetence();

    // Lista de bases de rateio dinâmicas salvas no DB
    const [dynamicBases, setDynamicBases] = useState([]);

    const [fileState, setFileState] = useState({});
    const [configFilenames, setConfigFilenames] = useState({});
    const [baseData, setBaseData] = useState({});

    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState('');
    const [error, setError] = useState(null);

    // Competência (Mês/Ano) - use global competence or current month
    const [competencia, setCompetencia] = useState(globalCompetence || new Date().toISOString().slice(0, 7));

    // Estado para o formulário de Cadastro de Nova Base
    const [newBaseForm, setNewBaseForm] = useState({
        id: '', // Chave única (ex: 'computadores')
        name: '', // Nome amigável (ex: 'Base de Computadores')
        valueKey: '', // Nome da coluna no CSV (ex: 'Qtd_PC')
    });

    // Estado para nova linha
    const [newRowData, setNewRowData] = useState({
        'Centro de Custo': '',
    });

    const [ccOptions, setCcOptions] = useState([]);

    // Estados de Controle
    const [isRegistering, setIsRegistering] = useState(false);
    const [isReplicating, setIsReplicating] = useState(false);
    const [replicationTarget, setReplicationTarget] = useState('');

    // Sync with global competence
    useEffect(() => {
        if (globalCompetence && globalCompetence !== competencia) {
            setCompetencia(globalCompetence);
        }
    }, [globalCompetence]);

    useEffect(() => {
        fetchAllData();
    }, [competencia]); // Recarrega quando a competência muda

    const fetchAllData = async () => {
        setLoading(true);
        setError(null);

        try {
            // 1. Carrega a lista de bases de rateio cadastradas
            const resBases = await getConfig(DEFAULT_BASES_CONFIG_KEY);
            const bases = resBases.data || [];
            setDynamicBases(bases);

            const initialFilenames = {};

            // 2. Para cada base, carrega os dados da competência selecionada
            await Promise.all(bases.map(async (base) => {
                // Filename is less relevant now as data is per month, but we can keep it if we want to track last upload
                // For now, let's focus on the data.
                await fetchBaseData(base.id);
            }));

            // 3. Carrega centros de custo disponíveis
            const resCC = await getConfig('centros_custo');
            setCcOptions((resCC.data || []).map(cc => cc.nome).sort());

            setConfigFilenames(initialFilenames);

        } catch (err) {
            console.error("Erro ao carregar configurações ou dados:", err);
            setError('Erro ao carregar configurações ou dados iniciais. Verifique a conexão com o backend.');
        } finally {
            setLoading(false);
        }
    };

    const getBaseConfig = (baseId) => dynamicBases.find(b => b.id === baseId);

    const fetchBaseData = async (baseId) => {
        try {
            // Chama a nova rota GET com competência
            const response = await axios.get(`${API_URL}/auxiliary/${baseId}`, {
                params: { competencia }
            });

            const data = response.data;

            if (data.status === "Erro") {
                throw new Error(data.mensagem);
            }

            const baseConfig = getBaseConfig(baseId);
            const valueKey = baseConfig ? baseConfig.valueKey : 'Valor';

            setBaseData(prev => ({
                ...prev,
                [baseId]: (data.data || []).map(item => ({
                    'Centro de Custo': item['Centro de Custo'],
                    [valueKey]: parseFloat(item[valueKey]) || 0
                }))
            }));

        } catch (err) {
            console.error(`Falha ao carregar dados da base ${baseId}:`, err);
            // Define o erro no topo, mas não impede a renderização de outras bases
        }
    };

    const handleFileChange = (baseId, event) => {
        const file = event.target.files[0];
        setFileState(prev => ({
            ...prev,
            [baseId]: { file, filename: file ? file.name : '' }
        }));
    };

    const handleUpload = async (baseId) => {
        const fileStateObj = fileState[baseId] || { file: null, filename: '' };
        const { file, filename } = fileStateObj;

        if (!file) {
            setError(`Selecione um arquivo para a base ${baseId}.`);
            return;
        }

        setUploading(true);
        setStatus(`Enviando ${filename}...`);
        setError(null);

        try {
            // BETTER APPROACH: Read file content client-side and send to /api/auxiliary/salvar
            const text = await file.text();
            const lines = text.split('\n');
            const headers = lines[0].split(';').map(h => h.trim()); // Assuming CSV uses semicolon

            const baseConfig = getBaseConfig(baseId);
            const valueKey = baseConfig ? baseConfig.valueKey : 'Valor';

            // Simple CSV parser (robust enough for this context?)
            const parsedData = [];
            for (let i = 1; i < lines.length; i++) {
                if (!lines[i].trim()) continue;
                const values = lines[i].split(';');
                const row = {};
                headers.forEach((h, index) => {
                    row[h] = values[index]?.trim();
                });

                if (row['Centro de Custo']) {
                    parsedData.push({
                        'Centro de Custo': row['Centro de Custo'],
                        [valueKey]: parseFloat(row[valueKey]) || 0
                    });
                }
            }

            // Use local competence if available, otherwise fallback to global
            const uploadCompetencia = fileStateObj.competencia || competencia;

            await handleSaveBaseData(baseId, parsedData, uploadCompetencia);

            setStatus(`Base '${baseId}' ('${filename}') carregada para ${uploadCompetencia}.`);

            // Limpa o estado de arquivo selecionado
            setFileState(prev => ({
                ...prev,
                [baseId]: { file: null, filename: '' }
            }));

        } catch (err) {
            console.error(err);
            setError(`Falha no upload da base ${baseId}: ${err.message || 'Erro de processamento.'}`);
        } finally {
            setUploading(false);
            setTimeout(() => setStatus(''), 3000);
        }
    };

    // --- Lógica de Cadastro e Remoção de Base (Configuração) ---

    const handleRegisterBase = async () => {
        const { id, name, valueKey } = newBaseForm;
        const normalizedId = id.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, '');

        if (!normalizedId || !name.trim() || !valueKey.trim()) {
            setError("Todos os campos de cadastro são obrigatórios.");
            return;
        }

        if (dynamicBases.some(b => b.id === normalizedId)) {
            setError(`A chave única de ID '${normalizedId}' já existe.`);
            return;
        }

        const newBaseEntry = {
            id: normalizedId,
            name: name.trim(),
            valueKey: valueKey.trim(),
        };

        const updatedBases = [...dynamicBases, newBaseEntry];
        setSaving(true);

        try {
            // Salva a nova lista de bases configuradas no DB
            await saveConfig(DEFAULT_BASES_CONFIG_KEY, updatedBases);
            setDynamicBases(updatedBases);
            setNewBaseForm({ id: '', name: '', valueKey: '' });
            setIsRegistering(false);
            setStatus(`Nova base '${name}' registrada com sucesso!`);
        } catch (err) {
            setError(`Falha ao salvar a nova configuração de base: ${err.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setStatus(''), 3000);
        }
    };

    const handleDeleteBaseConfig = async (baseId) => {
        if (!window.confirm(`ATENÇÃO: Deletar a base '${baseId}' removerá a configuração. Os dados históricos podem permanecer no banco, mas a opção de uso será removida. Continuar?`)) {
            return;
        }

        const updatedBases = dynamicBases.filter(b => b.id !== baseId);
        setSaving(true);

        try {
            await saveConfig(DEFAULT_BASES_CONFIG_KEY, updatedBases);
            setDynamicBases(updatedBases);

            // Limpa estados locais
            setBaseData(prev => {
                const newState = { ...prev };
                delete newState[baseId];
                return newState;
            });

            setStatus(`Configuração da base '${baseId}' removida.`);

        } catch (err) {
            setError(`Falha ao remover a base: ${err.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setStatus(''), 3000);
        }
    };


    // --- Lógica de Edição de Dados ---

    const handleValueChange = (baseId, ccName, newValue) => {
        const baseConfig = getBaseConfig(baseId);
        const valueKey = baseConfig ? baseConfig.valueKey : 'Valor';

        setBaseData(prev => ({
            ...prev,
            [baseId]: prev[baseId].map(item =>
                item['Centro de Custo'] === ccName
                    ? { ...item, [valueKey]: parseFloat(newValue) || 0 }
                    : item
            )
        }));
    };

    // Lógica para DELETAR uma linha
    const handleDeleteRow = (baseId, ccName) => {
        if (!ccName || !window.confirm(`Tem certeza que deseja deletar a linha para o Centro de Custo: ${ccName}?`)) {
            return;
        }

        setBaseData(prev => ({
            ...prev,
            [baseId]: prev[baseId].filter(item => item['Centro de Custo'] !== ccName)
        }));
        setStatus(`Linha para ${ccName} removida. Clique em SALVAR para confirmar a alteração no DB.`);
    };

    // Lógica para ADICIONAR uma nova linha
    const handleNewRow = (baseId) => {
        const baseConfig = getBaseConfig(baseId);
        if (!baseConfig) return;

        const valueKey = baseConfig.valueKey;
        const centroCusto = newRowData['Centro de Custo'].trim();
        const value = newRowData[valueKey];

        if (!centroCusto) {
            setError("O Centro de Custo não pode ser vazio.");
            return;
        }

        // Verifica se o Centro de Custo já existe
        const exists = (baseData[baseId] || []).some(item =>
            item['Centro de Custo'].toUpperCase() === centroCusto.toUpperCase()
        );

        if (exists) {
            setError(`O Centro de Custo '${centroCusto}' já existe na base.`);
            return;
        }

        const newEntry = {
            'Centro de Custo': centroCusto,
            [valueKey]: parseFloat(value) || 0,
        };

        setBaseData(prev => ({
            ...prev,
            [baseId]: [...(prev[baseId] || []), newEntry]
        }));

        // Limpa o formulário de nova linha
        setNewRowData(prev => {
            const newState = { ...prev, 'Centro de Custo': '' };
            newState[valueKey] = 0;
            return newState;
        });
        setStatus(`Centro de Custo '${centroCusto}' adicionado. Clique em SALVAR para persistir.`);
        setError(null);
    };

    const handleSaveBaseData = async (baseId, dataToSave = null, uploadCompetencia = null) => {
        setSaving(true);
        setError(null);

        try {
            // Se não foi passado dataToSave, usa o estado atual
            const data = dataToSave || (baseData[baseId] || []).filter(item =>
                item['Centro de Custo'].trim() !== ''
            );

            const comp = uploadCompetencia || competencia;

            await axios.post(`${API_URL}/auxiliary/salvar`, {
                base_id: baseId,
                competencia: comp,
                data: data
            });

            // Recarrega os dados se for a competência atual
            if (comp === competencia) {
                await fetchBaseData(baseId);
            }

            setStatus(`Dados da base ${baseId} salvos para ${comp}!`);

        } catch (err) {
            setError(`Falha ao salvar a base ${baseId}: ${err.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setStatus(''), 3000);
        }
    };

    const handleReplicateData = async (baseId) => {
        if (!replicationTarget) {
            setError("Selecione uma competência de destino.");
            return;
        }

        if (replicationTarget === competencia) {
            setError("A competência de destino deve ser diferente da atual.");
            return;
        }

        setSaving(true);
        try {
            await axios.post(`${API_URL}/auxiliary/replicate`, {
                base_id: baseId,
                source_competencia: competencia,
                target_competencia: replicationTarget
            });

            setStatus(`Dados replicados com sucesso para ${replicationTarget}!`);
            setIsReplicating(false);
            setReplicationTarget('');

        } catch (err) {
            setError(`Erro ao replicar: ${err.response?.data?.mensagem || err.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setStatus(''), 3000);
        }
    };

    const handleReplicateAll = async () => {
        if (!replicationTarget) {
            setError("Selecione uma competência de destino.");
            return;
        }

        if (replicationTarget === competencia) {
            setError("A competência de destino deve ser diferente da atual.");
            return;
        }

        if (!window.confirm(`ATENÇÃO: Isso irá copiar TODAS as tabelas de ${competencia} para ${replicationTarget}. Dados existentes em ${replicationTarget} serão sobrescritos. Continuar?`)) {
            return;
        }

        setSaving(true);
        try {
            const response = await axios.post(`${API_URL}/auxiliary/replicate_all`, {
                source_competencia: competencia,
                target_competencia: replicationTarget
            });

            setStatus(response.data.mensagem || `Todas as tabelas foram replicadas para ${replicationTarget}!`);
            setIsReplicating(false);
            setReplicationTarget('');

        } catch (err) {
            setError(`Erro ao replicar tudo: ${err.response?.data?.mensagem || err.message}`);
        } finally {
            setSaving(false);
            setTimeout(() => setStatus(''), 3000);
        }
    };

    // Novo handler para forçar a visualização da tabela vazia (edição manual)
    const handleEnableManualEdit = (baseId) => {
        const baseConfig = getBaseConfig(baseId);
        if (!baseConfig) return;

        setBaseData(prev => ({
            ...prev,
            [baseId]: prev[baseId] === undefined ? [] : prev[baseId]
        }));

        setStatus(`Edição manual habilitada para a base '${baseConfig.name}'.`);
    };

    // --- Componentes e Visualização ---

    const getStatusText = (baseId) => {
        const dataLoaded = (baseData[baseId] || []).length > 0;
        const baseHasBeenTouched = baseData[baseId] !== undefined;

        if (dataLoaded) {
            return (
                <Chip icon={<CheckCircleIcon />} label={`Dados carregados para ${competencia}`} color="success" size="small" variant="outlined" />
            );
        }
        if (baseHasBeenTouched && !dataLoaded) {
            return (
                <Chip icon={<WarningIcon />} label={`Base Vazia em ${competencia}`} color="warning" size="small" variant="outlined" />
            );
        }
        return (
            <Chip label="Nenhum dado carregado" color="default" size="small" variant="outlined" />
        );
    };


    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                        <TableChartIcon sx={{ mr: 2, color: 'primary.main' }} /> Bases Auxiliares
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Gerencie bases de rateio por competência.
                    </Typography>
                </Box>
                <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', borderRadius: 2 }}>
                    <CalendarIcon sx={{ color: 'action.active', mr: 1 }} />
                    <Typography variant="body2" sx={{ mr: 1, fontWeight: 'bold' }}>Competência:</Typography>
                    <TextField
                        type="month"
                        value={competencia}
                        onChange={(e) => setCompetencia(e.target.value)}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                    />
                </Paper>
            </Box>

            {/* --- SEÇÃO DE CADASTRO DE NOVA BASE --- */}
            <Paper sx={{ p: 3, mb: 4, borderLeft: 6, borderColor: 'warning.main' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                        <TagIcon sx={{ mr: 1, color: 'warning.main' }} /> Cadastro de Nova Base de Rateio
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<CopyIcon />}
                            onClick={() => {
                                setIsReplicating('ALL');
                                setReplicationTarget('');
                            }}
                            size="small"
                        >
                            Replicar Tudo
                        </Button>
                        <Button
                            variant="contained"
                            color="warning"
                            onClick={() => setIsRegistering(!isRegistering)}
                            size="small"
                        >
                            {isRegistering ? 'Ocultar Formulário' : 'Cadastrar Nova Base'}
                        </Button>
                    </Stack>
                </Box>

                {isReplicating === 'ALL' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2">Replicar <b>TODAS</b> as tabelas de <b>{competencia}</b> para:</Typography>
                            <TextField
                                type="month"
                                value={replicationTarget}
                                onChange={(e) => setReplicationTarget(e.target.value)}
                                size="small"
                                sx={{ width: 150 }}
                            />
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleReplicateAll}
                            >
                                Confirmar Cópia em Massa
                            </Button>
                            <Button
                                size="small"
                                onClick={() => setIsReplicating(false)}
                            >
                                Cancelar
                            </Button>
                        </Box>
                    </Alert>
                )}

                {isRegistering && (
                    <Grid container spacing={2} alignItems="flex-end" sx={{ mt: 1 }}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="ID Único (Chave)"
                                value={newBaseForm.id}
                                onChange={(e) => setNewBaseForm(prev => ({ ...prev, id: e.target.value.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, '') }))}
                                placeholder="ex: computadores"
                                size="small"
                                helperText="Usado internamente pelo sistema"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Nome Amigável"
                                value={newBaseForm.name}
                                onChange={(e) => setNewBaseForm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="ex: Base de Computadores"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Nome da Coluna de Valor (no CSV)"
                                value={newBaseForm.valueKey}
                                onChange={(e) => setNewBaseForm(prev => ({ ...prev, valueKey: e.target.value }))}
                                placeholder="ex: Qtd_PC"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                onClick={handleRegisterBase}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            >
                                {saving ? 'Salvando Config...' : 'Salvar Configuração'}
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Paper>

            {/* --- LISTAGEM DE BASES DINÂMICAS --- */}

            {dynamicBases.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default', borderStyle: 'dashed', borderWidth: 1 }}>
                    <Typography color="text.secondary">Nenhuma base de rateio dinâmica cadastrada ainda. Use o formulário acima para começar.</Typography>
                </Paper>
            ) : (
                dynamicBases.map((base) => (
                    <Paper key={base.id} sx={{ p: 3, mb: 4, borderLeft: 6, borderColor: 'primary.main' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                                <LaptopIcon sx={{ mr: 2, color: 'primary.main', fontSize: 30 }} /> {base.name} (Chave: {base.id})
                            </Typography>
                            <Box>
                                <Tooltip title="Replicar dados para outra competência">
                                    <IconButton
                                        onClick={() => { setIsReplicating(base.id); setReplicationTarget(''); }}
                                        color="primary"
                                    >
                                        <CopyIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Deletar Base">
                                    <IconButton
                                        onClick={() => handleDeleteBaseConfig(base.id)}
                                        disabled={saving}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {isReplicating === base.id && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2">Replicar dados de <b>{competencia}</b> para:</Typography>
                                    <TextField
                                        type="month"
                                        value={replicationTarget}
                                        onChange={(e) => setReplicationTarget(e.target.value)}
                                        size="small"
                                        sx={{ width: 150 }}
                                    />
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleReplicateData(base.id)}
                                    >
                                        Confirmar Cópia
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => setIsReplicating(false)}
                                    >
                                        Cancelar
                                    </Button>
                                </Box>
                            </Alert>
                        )}

                        <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Coluna de Valor: <Box component="span" sx={{ color: 'primary.main', fontFamily: 'monospace', fontWeight: 'bold' }}>{base.valueKey}</Box>
                            </Typography>
                            {getStatusText(base.id)}
                        </Box>

                        {/* Bloco de Upload */}
                        <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    type="month"
                                    label="Competência de Upload"
                                    value={fileState[base.id]?.competencia || competencia}
                                    onChange={(e) => setFileState(prev => ({
                                        ...prev,
                                        [base.id]: { ...(prev[base.id] || {}), competencia: e.target.value }
                                    }))}
                                    size="small"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    fullWidth
                                    startIcon={<CloudUploadIcon />}
                                >
                                    Selecionar CSV
                                    <input
                                        type="file"
                                        hidden
                                        accept=".csv"
                                        onChange={(e) => handleFileChange(base.id, e)}
                                    />
                                </Button>
                                {fileState[base.id]?.filename && (
                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, ml: 1 }}>
                                        {fileState[base.id].filename}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => handleUpload(base.id)}
                                    disabled={!fileState[base.id]?.file || uploading}
                                    startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                                >
                                    {uploading ? 'Processando...' : 'Importar CSV'}
                                </Button>
                            </Grid>
                        </Grid>

                        {/* Bloco de Visualização e Edição */}
                        {((baseData[base.id] || []).length > 0 || baseData[base.id] !== undefined) && (
                            <Box sx={{ mt: 4 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                                    <EditIcon sx={{ mr: 1, color: 'primary.main' }} /> Dados em {competencia}
                                </Typography>
                                <TableContainer sx={{ maxHeight: 400, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>Centro de Custo</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>{base.valueKey}</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 'bold', width: '20%' }}>Ações</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(baseData[base.id] || []).map((item, index) => (
                                                <TableRow key={item['Centro de Custo'] || index} hover>
                                                    <TableCell>{item['Centro de Custo']}</TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            type="number"
                                                            value={item[base.valueKey] !== undefined ? item[base.valueKey] : ''}
                                                            onChange={(e) => handleValueChange(base.id, item['Centro de Custo'], e.target.value)}
                                                            size="small"
                                                            fullWidth
                                                            variant="standard"
                                                            InputProps={{ disableUnderline: true }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            onClick={() => handleDeleteRow(base.id, item['Centro de Custo'])}
                                                            color="error"
                                                            size="small"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* Bloco de ADIÇÃO DE NOVA LINHA */}
                                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
                                    <Grid container spacing={2} alignItems="flex-end">
                                        <Grid item xs={12} md={5}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Centro de Custo</InputLabel>
                                                <Select
                                                    value={newRowData['Centro de Custo']}
                                                    label="Centro de Custo"
                                                    onChange={(e) => setNewRowData(prev => ({ ...prev, 'Centro de Custo': e.target.value }))}
                                                >
                                                    <MenuItem value=""><em>Selecione...</em></MenuItem>
                                                    {ccOptions.map(cc => (
                                                        <MenuItem key={cc} value={cc}>{cc}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <TextField
                                                fullWidth
                                                type="number"
                                                label={base.valueKey}
                                                value={newRowData[base.valueKey] !== undefined ? newRowData[base.valueKey] : 0}
                                                onChange={(e) => setNewRowData(prev => ({ ...prev, [base.valueKey]: e.target.value }))}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={2}>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                onClick={() => handleNewRow(base.id)}
                                                startIcon={<AddIcon />}
                                                size="small"
                                            >
                                                Adicionar
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>

                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={() => handleSaveBaseData(base.id)}
                                        disabled={saving || (baseData[base.id] || []).length === 0}
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                    >
                                        {saving ? 'Salvando...' : 'Salvar Alterações'}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {!(baseData[base.id] || []).length > 0 && baseData[base.id] === undefined && (
                            <Box sx={{ mt: 4, textAlign: 'center', p: 4, border: 1, borderColor: 'divider', borderStyle: 'dashed', borderRadius: 1 }}>
                                <Typography color="text.secondary" gutterBottom>Nenhum dado encontrado para {competencia}.</Typography>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    onClick={() => handleEnableManualEdit(base.id)}
                                    startIcon={<EditIcon />}
                                >
                                    Iniciar Edição Manual
                                </Button>
                            </Box>
                        )}
                    </Paper>
                ))
            )}

            {status && (
                <Alert severity="success" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999, boxShadow: 3 }}>
                    {status}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999, boxShadow: 3 }}>
                    {error}
                </Alert>
            )}

        </Box>
    );
};

export default TabelasAuxiliaresPage;
